import React, { useState, useEffect } from "react";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Form,
	FormGroup,
	Label,
	Input,
	Alert,
	Spinner,
	Row,
	Col,
	CustomInput,
	Card,
	CardBody,
	CardHeader,
	Badge,
	Table,
	Progress
} from "reactstrap";
import {
	createFormulationAPI,
	generateFormulationAPI,
	CreateFormulationRequest,
	GenerateFormulationRequest
} from "../../services/formulationService";
import "../../assets/scss/modals/_formulation-modal.scss";

interface FormulationModalProps {
	isOpen: boolean;
	toggle: () => void;
	onFormulationCreated?: (data: any) => void;
	prefillData?: {
		description?: string;
		productType?: string;
	};
}

const FormulationModal: React.FC<FormulationModalProps> = ({
	isOpen,
	toggle,
	onFormulationCreated,
	prefillData
}) => {
	const [mode, setMode] = useState<"quick" | "detailed">("quick");
	const [description, setDescription] = useState("");
	const [productType, setProductType] = useState("");

	// Prefill data when modal opens with prefill data
	useEffect(() => {
		if (isOpen && prefillData) {
			if (prefillData.description) {
				setDescription(prefillData.description);
			}
			if (prefillData.productType) {
				setProductType(prefillData.productType);
			}
		}
	}, [isOpen, prefillData]);
	const [servingSize, setServingSize] = useState<number>(0);
	const [targetNutrition, setTargetNutrition] = useState({
		calories: "",
		protein: "",
		carbs: "",
		fat: "",
		fiber: ""
	});
	const [maxCost, setMaxCost] = useState<number | undefined>(undefined);
	const [allergens, setAllergens] = useState<string[]>([]);
	const [dietary, setDietary] = useState<string[]>([]);
	const [isGenerating, setIsGenerating] = useState(false);
	const [formulationResult, setFormulationResult] = useState<any>(null);
	const [error, setError] = useState<string | null>(null);
	const [showResult, setShowResult] = useState(false);

	const commonAllergens = [
		"Milk",
		"Eggs",
		"Fish",
		"Shellfish",
		"Tree Nuts",
		"Peanuts",
		"Wheat",
		"Soybeans"
	];

	const dietaryOptions = [
		"Vegan",
		"Vegetarian",
		"Gluten-Free",
		"Keto",
		"Paleo",
		"Organic",
		"Non-GMO",
		"Halal",
		"Kosher"
	];

	const handleAllergenChange = (allergen: string, checked: boolean) => {
		if (checked) {
			setAllergens([...allergens, allergen]);
		} else {
			setAllergens(allergens.filter((a) => a !== allergen));
		}
	};

	const handleDietaryChange = (diet: string, checked: boolean) => {
		if (checked) {
			setDietary([...dietary, diet]);
		} else {
			setDietary(dietary.filter((d) => d !== diet));
		}
	};

	const handleGenerate = async () => {
		if (mode === "quick") {
			if (!description.trim()) {
				setError("Please enter a description");
				return;
			}

			setIsGenerating(true);
			setError(null);
			setFormulationResult(null);
			setShowResult(false);

			try {
				const request: GenerateFormulationRequest = {
					description: description.trim(),
					max_cost: maxCost
				};

				const result = await generateFormulationAPI(request);

				if (!result.success) {
					throw new Error(result.error || "Failed to generate formulation");
				}

				setFormulationResult(result.data);
				setShowResult(true);
			} catch (err) {
				console.error("Error generating formulation:", err);
				const errorMessage =
					err instanceof Error
						? err.message
						: "Failed to generate formulation. Please try again.";
				setError(errorMessage);
				setFormulationResult(null);
				setShowResult(false);
			} finally {
				setIsGenerating(false);
			}
		} else {
			// Detailed mode
			if (!description.trim()) {
				setError("Please enter a description");
				return;
			}

			if (!productType.trim()) {
				setError("Please enter a product type");
				return;
			}

			if (!servingSize || servingSize <= 0) {
				setError("Please enter a valid serving size");
				return;
			}

			setIsGenerating(true);
			setError(null);
			setFormulationResult(null);
			setShowResult(false);

			try {
				const request: CreateFormulationRequest = {
					description: description.trim(),
					product_type: productType.trim(),
					serving_size: servingSize,
					target_nutrition: {
						calories: targetNutrition.calories ? parseFloat(targetNutrition.calories) : undefined,
						protein: targetNutrition.protein ? parseFloat(targetNutrition.protein) : undefined,
						carbs: targetNutrition.carbs ? parseFloat(targetNutrition.carbs) : undefined,
						fat: targetNutrition.fat ? parseFloat(targetNutrition.fat) : undefined,
						fiber: targetNutrition.fiber ? parseFloat(targetNutrition.fiber) : undefined
					},
					constraints: {
						max_cost: maxCost,
						allergens: allergens.length > 0 ? allergens : undefined,
						dietary: dietary.length > 0 ? dietary : undefined
					}
				};

				const result = await createFormulationAPI(request);

				if (!result.success) {
					throw new Error(result.error || "Failed to create formulation");
				}

				setFormulationResult(result.data);
				setShowResult(true);
			} catch (err) {
				console.error("Error creating formulation:", err);
				const errorMessage =
					err instanceof Error
						? err.message
						: "Failed to create formulation. Please try again.";
				setError(errorMessage);
				setFormulationResult(null);
				setShowResult(false);
			} finally {
				setIsGenerating(false);
			}
		}
	};

	const handleClose = () => {
		setDescription("");
		setProductType("");
		setServingSize(0);
		setTargetNutrition({
			calories: "",
			protein: "",
			carbs: "",
			fat: "",
			fiber: ""
		});
		setMaxCost(undefined);
		setAllergens([]);
		setDietary([]);
		setFormulationResult(null);
		setError(null);
		setShowResult(false);
		setMode("quick");
		toggle();
	};

	const handleNewFormulation = () => {
		setFormulationResult(null);
		setShowResult(false);
		setError(null);
	};

	const handleUseFormulation = () => {
		if (formulationResult && onFormulationCreated) {
			onFormulationCreated(formulationResult);
		}
		handleClose();
	};

	return (
		<Modal
			isOpen={isOpen}
			toggle={handleClose}
			size="xl"
			className="formulation-modal"
		>
			<ModalHeader toggle={handleClose}>Create Formulation</ModalHeader>
			<ModalBody>
				{!showResult ? (
					<div className="formulation-form-container">
						{/* Mode Toggle */}
						<div className="mode-toggle">
							<Button
								color={mode === "quick" ? "primary" : "secondary"}
								outline={mode !== "quick"}
								onClick={() => setMode("quick")}
								className="mode-toggle-btn"
							>
								Quick Generate
							</Button>
							<Button
								color={mode === "detailed" ? "primary" : "secondary"}
								outline={mode !== "detailed"}
								onClick={() => setMode("detailed")}
								className="mode-toggle-btn"
							>
								Detailed Form
							</Button>
						</div>

						<Form className="formulation-form">
							<FormGroup className="form-section">
								<Label for="description" className="form-label">
									Description <span className="required-asterisk">*</span>
								</Label>
								<Input
									type="textarea"
									id="description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Describe the formulation you want to create (e.g., 'High protein energy bar', 'Low sugar beverage')"
									rows={3}
									disabled={isGenerating}
									className="form-control-custom"
								/>
							</FormGroup>

							{mode === "quick" && (
								<FormGroup className="form-section">
									<Label for="quickMaxCost" className="form-label">
										Max Cost <span className="optional-badge">(Optional)</span>
									</Label>
									<Input
										type="number"
										id="quickMaxCost"
										value={maxCost || ""}
										onChange={(e) =>
											setMaxCost(
												e.target.value ? parseFloat(e.target.value) : undefined
											)
										}
										placeholder="e.g., 2.50"
										min="0"
										step="0.01"
										disabled={isGenerating}
										className="form-control-custom"
									/>
								</FormGroup>
							)}

							{mode === "detailed" && (
								<>
									<div className="form-section-divider"></div>

									<div className="form-section">
										<Row>
											<Col md={6}>
												<FormGroup>
													<Label for="productType" className="form-label">
														Product Type <span className="required-asterisk">*</span>
													</Label>
													<Input
														type="text"
														id="productType"
														value={productType}
														onChange={(e) => setProductType(e.target.value)}
														placeholder="e.g., Energy Bar, Beverage"
														disabled={isGenerating}
														className="form-control-custom"
													/>
												</FormGroup>
											</Col>
											<Col md={6}>
												<FormGroup>
													<Label for="servingSize" className="form-label">
														Serving Size <span className="required-asterisk">*</span>
													</Label>
													<Input
														type="number"
														id="servingSize"
														value={servingSize || ""}
														onChange={(e) =>
															setServingSize(parseFloat(e.target.value) || 0)
														}
														placeholder="e.g., 50"
														min="0"
														step="0.1"
														disabled={isGenerating}
														className="form-control-custom"
													/>
												</FormGroup>
											</Col>
										</Row>
									</div>

									<div className="form-section-divider"></div>

									<div className="form-section">
										<h6 className="section-title">
											Target Nutrition <span className="optional-badge">(Optional)</span>
										</h6>
										<Row>
											<Col md={6}>
												<FormGroup>
													<Label for="calories" className="form-label">Calories (kcal)</Label>
													<Input
														type="number"
														id="calories"
														value={targetNutrition.calories}
														onChange={(e) =>
															setTargetNutrition({
																...targetNutrition,
																calories: e.target.value
															})
														}
														placeholder="e.g., 200"
														min="0"
														step="0.1"
														disabled={isGenerating}
														className="form-control-custom"
													/>
												</FormGroup>
											</Col>
											<Col md={6}>
												<FormGroup>
													<Label for="protein" className="form-label">Protein (g)</Label>
													<Input
														type="number"
														id="protein"
														value={targetNutrition.protein}
														onChange={(e) =>
															setTargetNutrition({
																...targetNutrition,
																protein: e.target.value
															})
														}
														placeholder="e.g., 20"
														min="0"
														step="0.1"
														disabled={isGenerating}
														className="form-control-custom"
													/>
												</FormGroup>
											</Col>
										</Row>
										<Row>
											<Col md={4}>
												<FormGroup>
													<Label for="carbs" className="form-label">Carbs (g)</Label>
													<Input
														type="number"
														id="carbs"
														value={targetNutrition.carbs}
														onChange={(e) =>
															setTargetNutrition({
																...targetNutrition,
																carbs: e.target.value
															})
														}
														placeholder="e.g., 30"
														min="0"
														step="0.1"
														disabled={isGenerating}
														className="form-control-custom"
													/>
												</FormGroup>
											</Col>
											<Col md={4}>
												<FormGroup>
													<Label for="fat" className="form-label">Fat (g)</Label>
													<Input
														type="number"
														id="fat"
														value={targetNutrition.fat}
														onChange={(e) =>
															setTargetNutrition({
																...targetNutrition,
																fat: e.target.value
															})
														}
														placeholder="e.g., 5"
														min="0"
														step="0.1"
														disabled={isGenerating}
														className="form-control-custom"
													/>
												</FormGroup>
											</Col>
											<Col md={4}>
												<FormGroup>
													<Label for="fiber" className="form-label">Fiber (g)</Label>
													<Input
														type="number"
														id="fiber"
														value={targetNutrition.fiber}
														onChange={(e) =>
															setTargetNutrition({
																...targetNutrition,
																fiber: e.target.value
															})
														}
														placeholder="e.g., 5"
														min="0"
														step="0.1"
														disabled={isGenerating}
														className="form-control-custom"
													/>
												</FormGroup>
											</Col>
										</Row>
									</div>

									<div className="form-section-divider"></div>

									<div className="form-section">
										<h6 className="section-title">
											Constraints <span className="optional-badge">(Optional)</span>
										</h6>
										<FormGroup>
											<Label for="maxCost" className="form-label">Max Cost</Label>
											<Input
												type="number"
												id="maxCost"
												value={maxCost || ""}
												onChange={(e) =>
													setMaxCost(
														e.target.value ? parseFloat(e.target.value) : undefined
													)
												}
												placeholder="e.g., 2.50"
												min="0"
												step="0.01"
												disabled={isGenerating}
												className="form-control-custom"
											/>
										</FormGroup>
									</div>

									<div className="form-section-divider"></div>

									<div className="form-section">
										<FormGroup>
											<Label className="form-label section-label">Allergens to Avoid</Label>
											<div className="checkbox-group">
												{commonAllergens.map((allergen) => (
													<CustomInput
														key={allergen}
														type="checkbox"
														id={`allergen-${allergen}`}
														label={allergen}
														checked={allergens.includes(allergen)}
														onChange={(e) =>
															handleAllergenChange(allergen, e.target.checked)
														}
														disabled={isGenerating}
														className="checkbox-item"
													/>
												))}
											</div>
										</FormGroup>
									</div>

									<div className="form-section-divider"></div>

									<div className="form-section">
										<FormGroup>
											<Label className="form-label section-label">Dietary Requirements</Label>
											<div className="checkbox-group">
												{dietaryOptions.map((diet) => (
													<CustomInput
														key={diet}
														type="checkbox"
														id={`dietary-${diet}`}
														label={diet}
														checked={dietary.includes(diet)}
														onChange={(e) => handleDietaryChange(diet, e.target.checked)}
														disabled={isGenerating}
														className="checkbox-item"
													/>
												))}
											</div>
										</FormGroup>
									</div>
								</>
							)}
						</Form>

						{error && (
							<Alert color="danger" className="mt-3">
								{error}
							</Alert>
						)}

						{isGenerating && (
							<div className="text-center mt-3">
								<Spinner color="primary" />
								<p className="mt-2">
									{mode === "quick"
										? "Generating formulation with AI..."
										: "Creating formulation with AI..."}
								</p>
							</div>
						)}
					</div>
				) : (
					<div className="formulation-result-container">
						<Alert color="success" className="mb-3">
							<strong>Formulation {mode === "quick" ? "Generated" : "Created"} Successfully!</strong>
						</Alert>
						{formulationResult && (
							<div className="formulation-result">
								{/* Product Overview */}
								<Card className="mb-3">
									<CardBody>
										<h4 className="mb-2">{formulationResult.name || "Formulation"}</h4>
										{formulationResult.description && (
											<p className="text-muted mb-3">{formulationResult.description}</p>
										)}
										{formulationResult.status && (
											<Badge color={formulationResult.status === "draft" ? "secondary" : "success"} className="mr-2">
												{formulationResult.status}
											</Badge>
										)}
									</CardBody>
								</Card>

								{/* Ingredients Section */}
								{formulationResult.ingredients && formulationResult.ingredients.length > 0 && (
									<Card className="mb-3">
										<CardHeader>
											<h5 className="mb-0">Ingredients</h5>
										</CardHeader>
										<CardBody>
											<div className="table-responsive">
												<Table striped hover>
													<thead>
														<tr>
															<th>Ingredient</th>
															<th>Percentage</th>
															<th>Weight</th>
															<th>Cost</th>
															<th>Rationale</th>
														</tr>
													</thead>
													<tbody>
														{formulationResult.ingredients.map((ingredient: any, index: number) => (
															<tr key={ingredient.ingredient_id || index}>
																<td>
																	<strong>{ingredient.name}</strong>
																	{ingredient.nutritional_contribution && (
																		<div className="mt-2">
																			<small className="text-muted">
																				{ingredient.nutritional_contribution.protein && (
																					<span className="mr-2">Protein: {ingredient.nutritional_contribution.protein}g</span>
																				)}
																				{ingredient.nutritional_contribution.carbs && (
																					<span className="mr-2">Carbs: {ingredient.nutritional_contribution.carbs}g</span>
																				)}
																				{ingredient.nutritional_contribution.fat && (
																					<span className="mr-2">Fat: {ingredient.nutritional_contribution.fat}g</span>
																				)}
																				{ingredient.nutritional_contribution.fiber && (
																					<span>Fiber: {ingredient.nutritional_contribution.fiber}g</span>
																				)}
																			</small>
																		</div>
																	)}
																</td>
																<td>{ingredient.percentage}%</td>
																<td>{ingredient.weight} {ingredient.unit || "g"}</td>
																<td>${typeof ingredient.cost === 'number' ? ingredient.cost.toFixed(2) : "0.00"}</td>
																<td>
																	<small className="text-muted">{ingredient.rationale}</small>
																</td>
															</tr>
														))}
													</tbody>
												</Table>
											</div>
										</CardBody>
									</Card>
								)}

								{/* Target Profile */}
								{formulationResult.target_profile && (
									<Card className="mb-3">
										<CardHeader>
											<h5 className="mb-0">Target Profile</h5>
										</CardHeader>
										<CardBody>
											<Row>
												<Col md={6}>
													<p><strong>Product Type:</strong> {formulationResult.target_profile.product_type}</p>
													<p><strong>Serving Size:</strong> {formulationResult.target_profile.serving_size} {formulationResult.target_profile.serving_size ? "g" : ""}</p>
												</Col>
												<Col md={6}>
													{formulationResult.target_profile.target_nutrition && (
														<div>
															<strong>Target Nutrition:</strong>
															<ul className="list-unstyled mt-2">
																{formulationResult.target_profile.target_nutrition.calories && (
																	<li>Calories: {formulationResult.target_profile.target_nutrition.calories} kcal</li>
																)}
																{formulationResult.target_profile.target_nutrition.protein && (
																	<li>Protein: {formulationResult.target_profile.target_nutrition.protein}g</li>
																)}
																{formulationResult.target_profile.target_nutrition.carbs && (
																	<li>Carbs: {formulationResult.target_profile.target_nutrition.carbs}g</li>
																)}
																{formulationResult.target_profile.target_nutrition.fat && (
																	<li>Fat: {formulationResult.target_profile.target_nutrition.fat}g</li>
																)}
																{formulationResult.target_profile.target_nutrition.fiber && (
																	<li>Fiber: {formulationResult.target_profile.target_nutrition.fiber}g</li>
																)}
															</ul>
														</div>
													)}
												</Col>
											</Row>
											{formulationResult.target_profile.constraints && (
												<div className="mt-3">
													<strong>Constraints:</strong>
													{formulationResult.target_profile.constraints.max_cost && (
														<Badge color="info" className="ml-2">Max Cost: ${formulationResult.target_profile.constraints.max_cost}</Badge>
													)}
													{formulationResult.target_profile.constraints.allergens && formulationResult.target_profile.constraints.allergens.length > 0 && (
														<div className="mt-2">
															<strong>Allergens to Avoid:</strong>
															{formulationResult.target_profile.constraints.allergens.map((allergen: string, idx: number) => (
																<Badge key={idx} color="warning" className="ml-2">{allergen}</Badge>
															))}
														</div>
													)}
													{formulationResult.target_profile.constraints.dietary && formulationResult.target_profile.constraints.dietary.length > 0 && (
														<div className="mt-2">
															<strong>Dietary Requirements:</strong>
															{formulationResult.target_profile.constraints.dietary.map((diet: string, idx: number) => (
																<Badge key={idx} color="success" className="ml-2">{diet}</Badge>
															))}
														</div>
													)}
												</div>
											)}
										</CardBody>
									</Card>
								)}

								{/* Calculated Scores */}
								{formulationResult.calculated_scores && (
									<Card className="mb-3">
										<CardHeader>
											<h5 className="mb-0">Calculated Scores</h5>
										</CardHeader>
										<CardBody>
											<Row>
												<Col md={3}>
													<div className="text-center mb-3">
														<h6 className="text-muted">Overall Score</h6>
														<div className="score-circle">
															<h2 className="mb-0">
																{Math.round(formulationResult.calculated_scores.overall_score || 0)}
															</h2>
														</div>
													</div>
												</Col>
												<Col md={9}>
													{formulationResult.calculated_scores.nutrition_score && (
														<div className="mb-3">
															<div className="d-flex justify-content-between mb-1">
																<span><strong>Nutrition Score:</strong></span>
																<span>{Math.round(formulationResult.calculated_scores.nutrition_score)}</span>
															</div>
															<Progress value={formulationResult.calculated_scores.nutrition_score} color="success" />
														</div>
													)}
													{formulationResult.calculated_scores.roi_score && (
														<div className="mb-3">
															<div className="d-flex justify-content-between mb-1">
																<span><strong>ROI Score:</strong></span>
																<span>{Math.round(formulationResult.calculated_scores.roi_score)}</span>
															</div>
															<Progress value={formulationResult.calculated_scores.roi_score} color="info" />
														</div>
													)}
													{formulationResult.calculated_scores.sustainability_score && (
														<div className="mb-3">
															<div className="d-flex justify-content-between mb-1">
																<span><strong>Sustainability Score:</strong></span>
																<span>{Math.round(formulationResult.calculated_scores.sustainability_score)}</span>
															</div>
															<Progress value={formulationResult.calculated_scores.sustainability_score} color="warning" />
														</div>
													)}
												</Col>
											</Row>

											{formulationResult.calculated_scores.breakdown && (
												<div className="mt-4">
													<h6>Score Breakdown</h6>
													<Row>
														{formulationResult.calculated_scores.breakdown.nutrition && (
															<Col md={4}>
																<strong>Nutrition:</strong>
																<ul className="list-unstyled mt-2">
																	{Object.entries(formulationResult.calculated_scores.breakdown.nutrition).map(([key, value]: [string, any]) => (
																		<li key={key}>
																			{key}: {typeof value === 'number' ? Math.round(value) : value}
																		</li>
																	))}
																</ul>
															</Col>
														)}
														{formulationResult.calculated_scores.breakdown.roi && (
															<Col md={4}>
																<strong>ROI:</strong>
																<ul className="list-unstyled mt-2">
																	{Object.entries(formulationResult.calculated_scores.breakdown.roi).map(([key, value]: [string, any]) => (
																		<li key={key}>
																			{key}: {typeof value === 'number' ? (key.includes('cost') || key.includes('price') ? `$${value.toFixed(2)}` : `${Math.round(value)}%`) : value}
																		</li>
																	))}
																</ul>
															</Col>
														)}
														{formulationResult.calculated_scores.breakdown.sustainability && (
															<Col md={4}>
																<strong>Sustainability:</strong>
																<ul className="list-unstyled mt-2">
																	{Object.entries(formulationResult.calculated_scores.breakdown.sustainability).map(([key, value]: [string, any]) => (
																		<li key={key}>
																			{key}: {typeof value === 'number' ? Math.round(value) : value}
																		</li>
																	))}
																</ul>
															</Col>
														)}
													</Row>
												</div>
											)}
										</CardBody>
									</Card>
								)}

								{/* Rationale */}
								{formulationResult.rationale && (
									<Card className="mb-3">
										<CardHeader>
											<h5 className="mb-0">Rationale</h5>
										</CardHeader>
										<CardBody>
											<p>{formulationResult.rationale}</p>
										</CardBody>
									</Card>
								)}
							</div>
						)}
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				{!showResult ? (
					<>
						<Button color="secondary" onClick={handleClose} disabled={isGenerating}>
							Cancel
						</Button>
						<Button
							color="primary"
							onClick={handleGenerate}
							disabled={
								isGenerating ||
								!description.trim() ||
								(mode === "detailed" && (!productType.trim() || !servingSize || servingSize <= 0))
							}
						>
							{isGenerating ? (
								<>
									<Spinner size="sm" className="mr-2" />
									{mode === "quick" ? "Generating..." : "Creating..."}
								</>
							) : mode === "quick" ? (
								"Generate Formulation"
							) : (
								"Create Formulation"
							)}
						</Button>
					</>
				) : (
					<>
						<Button color="secondary" onClick={handleNewFormulation}>
							Create Another
						</Button>
						<Button color="success" onClick={handleUseFormulation}>
							Use This Formulation
						</Button>
					</>
				)}
			</ModalFooter>
		</Modal>
	);
};

export default FormulationModal;



